import WaProductButton from "@/components/whatsapp/WaProductButton";
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Package, Plus, Check, ArrowLeft, QrCode, X, Printer, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { useSiteContent } from "@/context/SiteContentContext";
import { productTone } from "@/lib/productTone";

const USAGE_COLUMNS = [
    { key: "turbo_no", label: "Turbo No." },
    { key: "turbo_model", label: "Turbo model" },
    { key: "car_make", label: "Car make" },
    { key: "car_model", label: "Car model" },
    { key: "engine_displacement", label: "Engine" },
    { key: "engine_power", label: "Power (kW/HP)" },
    { key: "year_of_manufacture", label: "Years" },
    { key: "oem", label: "OEM" },
];

export default function ProductDetailPage() {
    const { productId } = useParams();
    const { user, refreshCart } = useAuth();
    const { format: fmtMoney } = useCurrency();
    const { t } = useTranslation();
    const { content } = useSiteContent();
    const nav = useNavigate();
    const [product, setProduct] = useState(null);
    const [qty, setQty] = useState(1);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    const [showQr, setShowQr] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    useEffect(() => {
        setNotFound(false);
        setProduct(null);
        setActiveImage(null);
        (async () => {
            try {
                const { data } = await api.get(`/products/${productId}`);
                setProduct(data);
                setActiveImage(data.image_url);
            } catch {
                setNotFound(true);
            }
        })();
    }, [productId]);

    // Arrow-key navigation through gallery (especially useful in lightbox)
    useEffect(() => {
        if (!product) return;
        const imgs = [product.image_url, ...(product.gallery || [])].filter(Boolean);
        if (imgs.length < 2) return;
        const onKey = (e) => {
            if (!lightboxOpen && document.activeElement?.tagName !== "BODY") return;
            const i = imgs.indexOf(activeImage);
            if (e.key === "ArrowRight") {
                e.preventDefault();
                setActiveImage(imgs[(i + 1) % imgs.length]);
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                setActiveImage(imgs[(i - 1 + imgs.length) % imgs.length]);
            } else if (e.key === "Escape" && lightboxOpen) {
                setLightboxOpen(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [product, activeImage, lightboxOpen]);

    const onAdd = async () => {
        if (!user || user === false) { nav("/login"); return; }
        setAdding(true);
        try {
            await api.post("/cart/add", { product_id: productId, quantity: qty });
            await refreshCart();
            setAdded(true);
            setTimeout(() => setAdded(false), 2000);
        } finally {
            setAdding(false);
        }
    };

    if (notFound) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 py-24 text-center" data-testid="product-not-found">
                <h1 className="font-display font-black text-4xl uppercase">{t("product.not_found")}</h1>
                <Link to="/catalog" className="btn-primary mt-6 inline-flex">
                    <ArrowLeft className="w-4 h-4" /> {t("product.back_to_catalog")}
                </Link>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="max-w-[1440px] mx-auto px-4 py-12">
                <div className="skeleton aspect-square max-w-md" />
            </div>
        );
    }

    const allImages = [product.image_url, ...(product.gallery || [])].filter(Boolean);
    const usage = Array.isArray(product.usage) ? product.usage : [];
    const specs = Array.isArray(product.specifications) ? product.specifications : [];

    return (
        <div className="pd-themed" style={productTone(content)} data-testid="product-detail-page">
            {/* Breadcrumb */}
            <div className="border-b border-slate-200 bg-slate-50/60">
                <div className="max-w-[1440px] mx-auto px-4 py-4">
                    <div className="text-xs font-mono text-slate-500">
                        <Link to="/" className="hover:text-[hsl(var(--primary))]">{t("catalog.breadcrumb_home")}</Link>
                        <span className="mx-2">/</span>
                        <Link to="/catalog" className="hover:text-[hsl(var(--primary))]">{t("catalog.breadcrumb_catalog")}</Link>
                        {product.category_id && (
                            <>
                                <span className="mx-2">/</span>
                                <Link to={`/catalog/${product.category_id}`} className="hover:text-[hsl(var(--primary))] uppercase">
                                    {product.category_name}
                                </Link>
                            </>
                        )}
                        <span className="mx-2">/</span>
                        <span className="text-slate-900 uppercase">{product.name}</span>
                    </div>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 py-10 grid md:grid-cols-[1.1fr_1fr] gap-10">
                {/* Image gallery */}
                <div>
                    <div className="border border-slate-200 bg-slate-50 aspect-square relative group rounded-2xl overflow-hidden shadow-[0_20px_50px_-30px_rgba(15,23,42,0.45)] fx-reveal" data-testid="product-image">
                        {activeImage ? (
                            <>
                                <img
                                    src={activeImage}
                                    alt={product.name}
                                    className="w-full h-full object-contain p-8 cursor-zoom-in"
                                    onError={(e) => (e.currentTarget.style.display = "none")}
                                    onClick={() => setLightboxOpen(true)}
                                    data-testid="product-image-main"
                                />
                                {allImages.length > 1 && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const i = allImages.indexOf(activeImage);
                                                setActiveImage(allImages[(i - 1 + allImages.length) % allImages.length]);
                                            }}
                                            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white border border-slate-300 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                            aria-label="Previous image"
                                            data-testid="gallery-prev"
                                        >
                                            <ChevronLeft className="w-5 h-5 text-slate-900" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const i = allImages.indexOf(activeImage);
                                                setActiveImage(allImages[(i + 1) % allImages.length]);
                                            }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white border border-slate-300 flex items-center justify-center transition-colors opacity-0 group-hover:opacity-100"
                                            aria-label="Next image"
                                            data-testid="gallery-next"
                                        >
                                            <ChevronRight className="w-5 h-5 text-slate-900" />
                                        </button>
                                        <div className="absolute bottom-3 right-3 font-mono text-[10px] text-slate-600 bg-white/90 px-2 py-0.5" data-testid="gallery-counter">
                                            {allImages.indexOf(activeImage) + 1} / {allImages.length}
                                        </div>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                                <Package className="w-24 h-24" />
                            </div>
                        )}
                        <div className="absolute top-3 left-3 font-mono text-[10px] text-slate-600 bg-white/80 backdrop-blur-md px-2.5 py-1 rounded-full border border-slate-200">
                            REF: #{product.id.slice(0, 8).toUpperCase()}
                        </div>
                    </div>
                    {/* Thumbnails */}
                    {allImages.length > 1 && (
                        <div className="mt-3 grid grid-cols-6 gap-2" data-testid="gallery-thumbnails">
                            {allImages.map((img, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImage(img)}
                                    className={`aspect-square border bg-slate-50 overflow-hidden rounded-xl transition-all hover:-translate-y-0.5 ${
                                        activeImage === img ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary)_/_0.15)]" : "border-slate-200 hover:border-slate-400"
                                    }`}
                                    data-testid={`thumb-${i}`}
                                >
                                    <img src={img} alt="" className="w-full h-full object-contain p-1" onError={(e) => (e.currentTarget.style.display = "none")} />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div>
                    <div className="label-kicker mb-2">{product.category_name}</div>
                    <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter leading-tight" data-testid="product-title">
                        {product.name}
                    </h1>

                    {(product.brand_name || product.aftermarket_brand_name) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="product-brand-block">
                            {product.brand_name && (
                                <Link
                                    to={`/catalog?brand_id=${product.brand_id}`}
                                    className="flex items-center gap-2 px-2.5 py-1 bg-slate-900 text-white hover:bg-[hsl(var(--primary))] transition-colors"
                                    title={t("product.see_all_brand")}
                                    data-testid="product-oem-brand-link"
                                >
                                    {product.brand_logo && (
                                        <img src={product.brand_logo} alt="" className="h-5 max-w-[48px] object-contain bg-white p-0.5" />
                                    )}
                                    <div className="leading-tight">
                                        <div className="text-[9px] font-mono uppercase opacity-70">{t("product.oem_brand")}</div>
                                        <div className="text-xs font-display font-bold uppercase">{product.brand_name}</div>
                                    </div>
                                </Link>
                            )}
                            {product.aftermarket_brand_name && (
                                <Link
                                    to={`/catalog?aftermarket_brand_id=${product.aftermarket_brand_id}`}
                                    className="flex items-center gap-2 px-2.5 py-1 bg-amber-100 border border-amber-300 text-amber-900 hover:bg-amber-200 transition-colors"
                                    title={t("product.see_all_brand")}
                                    data-testid="product-aftermarket-brand-link"
                                >
                                    {product.aftermarket_brand_logo && (
                                        <img src={product.aftermarket_brand_logo} alt="" className="h-5 max-w-[48px] object-contain" />
                                    )}
                                    <div className="leading-tight">
                                        <div className="text-[9px] font-mono uppercase opacity-70">{t("product.aftermarket_brand")}</div>
                                        <div className="text-xs font-display font-bold uppercase">{product.aftermarket_brand_name}</div>
                                    </div>
                                </Link>
                            )}
                        </div>
                    )}

                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                        <span
                            className={`text-xs font-mono font-bold uppercase px-2 py-1 ${
                                product.stock === "in_stock"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : "bg-slate-100 text-slate-700"
                            }`}
                            data-testid="product-stock-badge"
                        >
                            {product.stock === "in_stock" ? `● ${t("product.in_stock")}` : `○ ${t("product.on_order")}`}
                        </span>
                        {product.stock_qty !== undefined && product.stock_qty !== null && (
                            <span
                                className="text-xs font-mono font-bold text-slate-700 bg-slate-100 px-2 py-1"
                                data-testid="product-stock-qty"
                            >
                                {product.stock_qty} {t("product.pcs")}
                            </span>
                        )}
                        {product.part_number && (
                            <span className="text-xs font-mono text-slate-500">
                                {t("product.pn_short")}: <span className="text-slate-900 font-semibold">{product.part_number}</span>
                            </span>
                        )}
                    </div>

                    {/* Price block */}
                    <div className="mt-6 p-6 fx-dark-box relative overflow-hidden" data-testid="product-price-box">
                        <div className="hero-fx-glow absolute -top-20 -right-16 w-56 h-56 rounded-full pointer-events-none" aria-hidden="true" />
                        <div className="label-kicker text-slate-500 mb-2">{t("product.price")}</div>
                        {product.price ? (
                            <div className="font-display font-black text-4xl">{fmtMoney(product.price)}</div>
                        ) : (
                            <div className="font-mono text-sm text-slate-600">
                                {t("product.price_not_set")}
                            </div>
                        )}
                    </div>

                    {/* Qty + Add */}
                    <div className="mt-5 flex items-center gap-3">
                        <div className="flex items-center border border-slate-300 rounded-full overflow-hidden bg-white">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-11 hover:bg-slate-100 font-mono" data-testid="qty-minus">−</button>
                            <input
                                type="number"
                                value={qty}
                                min={1}
                                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || "1", 10)))}
                                className="w-14 h-11 text-center font-mono outline-none"
                                data-testid="qty-input"
                            />
                            <button onClick={() => setQty(qty + 1)} className="w-10 h-11 hover:bg-slate-100 font-mono" data-testid="qty-plus">+</button>
                        </div>
                        <button onClick={onAdd} disabled={adding} className="btn-primary flex-1" data-testid="add-to-cart-button">
                            {added ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                            {added ? t("product.added") : adding ? t("product.adding") : t("product.add_to_cart")}
                        </button>
                        <button onClick={() => setShowQr(true)} className="btn-outline" title={t("product.show_qr")} data-testid="show-qr-button">
                            <QrCode className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="mt-3"><WaProductButton product={product} /></div>

                    {/* Note */}
                    {product.note && (
                        <div className="mt-6 border-l-4 border-[hsl(var(--primary))] bg-slate-50 p-4 rounded-r-xl" data-testid="product-note">
                            <div className="label-kicker mb-1 text-slate-600">{t("product.note")}</div>
                            <p className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{product.note}</p>
                        </div>
                    )}

                    {/* Description */}
                    {product.description && (
                        <div className="mt-4">
                            <div className="label-kicker mb-2">{t("product.description")}</div>
                            <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{product.description}</p>
                        </div>
                    )}

                    {/* Specifications (gated by admin per user) */}
                    {specs.length > 0 && (
                        <div className="mt-6" data-testid="product-specs">
                            <div className="label-kicker mb-2">{t("product.specifications")}</div>
                            <table className="w-full text-sm font-mono border border-slate-200 fx-table">
                                <tbody>
                                    {specs.map((s, i) => (
                                        <tr key={i} className="border-b border-slate-200 last:border-b-0" data-testid={`spec-display-${i}`}>
                                            <td className="px-3 py-2 text-slate-500 bg-slate-50 w-2/5">
                                                {s.label}{s.unit ? <span className="text-slate-400">, {s.unit}</span> : null}
                                            </td>
                                            <td className="px-3 py-2 text-slate-900 font-semibold">{s.value || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Product info table */}
                    <div className="mt-6">
                        <div className="label-kicker mb-2">{t("product.product_info")}</div>
                        <table className="w-full text-sm font-mono border border-slate-200 fx-table">
                            <tbody>
                                <tr className="border-b border-slate-200">
                                    <td className="px-3 py-2 text-slate-500 bg-slate-50 w-1/3">{t("product.category")}</td>
                                    <td className="px-3 py-2 text-slate-900">{product.category_name || "-"}</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                    <td className="px-3 py-2 text-slate-500 bg-slate-50">{t("product.part_number")}</td>
                                    <td className="px-3 py-2 text-slate-900">{product.part_number || product.name}</td>
                                </tr>
                                <tr className="border-b border-slate-200">
                                    <td className="px-3 py-2 text-slate-500 bg-slate-50">{t("product.stock")}</td>
                                    <td className="px-3 py-2 text-slate-900">{product.stock === "in_stock" ? t("product.in_stock") : t("product.on_order")}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Usage / Cross-reference table - full width */}
            {usage.length > 0 && (
                <div className="max-w-[1440px] mx-auto px-4 pb-10" data-testid="product-usage">
                    <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-[0_10px_30px_-24px_rgba(15,23,42,0.4)]">
                        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                            <div className="label-kicker mb-1 text-slate-500">Usage · Cross-reference</div>
                            <div className="font-display font-bold uppercase">Fits the following turbos / vehicles</div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm font-mono">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        {USAGE_COLUMNS.map((c) => (
                                            <th key={c.key} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-600 uppercase tracking-wider">
                                                {c.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {usage.map((row, i) => (
                                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`usage-display-${i}`}>
                                            {USAGE_COLUMNS.map((c) => (
                                                <td key={c.key} className={`px-3 py-2 ${c.key === "turbo_no" ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                                                    {row[c.key] || "—"}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Lightbox */}
            {lightboxOpen && activeImage && (
                <div
                    className="fixed inset-0 bg-slate-900/95 z-50 flex items-center justify-center p-4"
                    onClick={() => setLightboxOpen(false)}
                    data-testid="image-lightbox"
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
                        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                        aria-label="Close"
                        data-testid="lightbox-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                    {allImages.length > 1 && (
                        <>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const i = allImages.indexOf(activeImage);
                                    setActiveImage(allImages[(i - 1 + allImages.length) % allImages.length]);
                                }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                aria-label="Previous"
                                data-testid="lightbox-prev"
                            >
                                <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const i = allImages.indexOf(activeImage);
                                    setActiveImage(allImages[(i + 1) % allImages.length]);
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                                aria-label="Next"
                                data-testid="lightbox-next"
                            >
                                <ChevronRight className="w-6 h-6" />
                            </button>
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-xs text-white/80 bg-black/40 px-3 py-1">
                                {allImages.indexOf(activeImage) + 1} / {allImages.length}
                            </div>
                        </>
                    )}
                    <img
                        src={activeImage}
                        alt={product.name}
                        className="max-w-full max-h-[90vh] object-contain select-none"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="lightbox-image"
                    />
                </div>
            )}

            {/* QR modal */}
            {showQr && (
                <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={() => setShowQr(false)} data-testid="qr-modal">
                    <div className="bg-white max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-display font-black uppercase text-lg">{t("product.qr_title")}</div>
                            <button onClick={() => setShowQr(false)} data-testid="qr-modal-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <img
                            src={`${API}/products/${product.id}/qr`}
                            alt={`QR for ${product.part_number}`}
                            className="w-full h-auto border border-slate-200"
                            data-testid="qr-code-image"
                            id="qr-code-print-target"
                        />
                        <div className="mt-4 text-center">
                            <div className="font-display font-black uppercase text-base">{product.part_number || "—"}</div>
                            <div className="font-mono text-xs text-slate-600 mt-1">{product.name}</div>
                        </div>
                        <p className="mt-4 text-center font-mono text-[10px] text-slate-500">
                            Scan to read: {product.part_number} · {product.name}
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                const w = window.open("", "_blank", "width=420,height=620");
                                if (!w) return;
                                w.document.write(`<!doctype html><html><head><title>QR · ${product.part_number || ""}</title>
                                <style>
                                  body{margin:0;padding:24px;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;text-align:center}
                                  img{width:100%;max-width:340px;height:auto;border:1px solid #ddd}
                                  h2{font-family:Arial,sans-serif;margin:14px 0 4px;text-transform:uppercase;letter-spacing:1px}
                                  p{margin:4px 0;font-size:11px;color:#666}
                                  @media print{body{padding:0}}
                                </style></head><body>
                                <img src="${API}/products/${product.id}/qr" />
                                <h2>${product.part_number || "—"}</h2>
                                <p>${(product.name || "").replace(/</g, "&lt;")}</p>
                                <script>window.onload=function(){setTimeout(function(){window.print();},400);};</script>
                                </body></html>`);
                                w.document.close();
                            }}
                            className="mt-3 w-full btn-outline justify-center"
                            data-testid="qr-print-button"
                        >
                            <Printer className="w-4 h-4" /> {t("product.qr_print")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
